import { createContext, useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

const SESSION_ACTIVE_FLAG = 'isSessionActive';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    // console.log("AuthContext: Initializing auth state...");
    const storedToken = localStorage.getItem("token");
    const isSessionPreviouslyActive = sessionStorage.getItem(SESSION_ACTIVE_FLAG);

    if (storedToken) {
      // console.log("AuthContext: Found token in localStorage.", { storedToken: !!storedToken, isSessionPreviouslyActive: !!isSessionPreviouslyActive });
      if (!isSessionPreviouslyActive) {
        // Token exists in localStorage, but no active session flag in sessionStorage.
        // This indicates the browser/tab was closed and reopened.
        // console.log("AuthContext: No active session flag. Logging out.");
        localStorage.removeItem("token");
        // No need to remove SESSION_ACTIVE_FLAG here as it should already be null/undefined
        setUser(null);
        setToken(null);
      } else {
        // Session was active (isSessionPreviouslyActive is true - likely a page reload), validate token
        // console.log("AuthContext: Active session flag found. Validating token.");
        try {
          const decoded = jwtDecode(storedToken);
          const currentTime = Date.now() / 1000;
          if (decoded.exp < currentTime) {
            // console.log("AuthContext: Token expired.");
            localStorage.removeItem("token");
            sessionStorage.removeItem(SESSION_ACTIVE_FLAG); // Clean up flag as session is now invalid
            setUser(null);
            setToken(null);
          } else {
            // console.log("AuthContext: Token valid. Setting user.");
            setUser(decoded);
            setToken(storedToken);
            // Ensure the flag remains for subsequent reloads within this session
            sessionStorage.setItem(SESSION_ACTIVE_FLAG, 'true');
          }
        } catch (err) {
          console.error("AuthContext: Invalid token during validation.", err);
          localStorage.removeItem("token");
          sessionStorage.removeItem(SESSION_ACTIVE_FLAG); // Clean up flag
          setUser(null);
          setToken(null);
        }
      }
    } else {
      // console.log("AuthContext: No token found in localStorage.");
      // No token in localStorage, ensure no active session flag either
      sessionStorage.removeItem(SESSION_ACTIVE_FLAG);
    }
    setLoadingAuth(false);
    // console.log("AuthContext: Auth loading finished.");
  }, []); // Run only once on initial component mount

  // The useEffect for 'beforeunload' is removed.
  // We rely on sessionStorage's natural behavior of clearing when the tab/browser is closed.

  const login = (jwt) => {
    try {
      const decoded = jwtDecode(jwt);
      localStorage.setItem("token", jwt); // Token stored in localStorage
      sessionStorage.setItem(SESSION_ACTIVE_FLAG, 'true'); // Mark session as active
      setUser(decoded);
      setToken(jwt);
      // console.log("AuthContext: Login successful. Token stored, session flag set.");
    } catch (err) {
      console.error("AuthContext: Token decoding failed during login:", err);
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
      sessionStorage.removeItem(SESSION_ACTIVE_FLAG);
    }
  };

  const logout = () => {
    // console.log("AuthContext: Logout function called. Clearing localStorage and session flag.");
    localStorage.removeItem("token");
    sessionStorage.removeItem(SESSION_ACTIVE_FLAG); // Clear session active flag
    setUser(null);
    setToken(null);
    // Navigation to /login should be handled by protected route components
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loadingAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);